import React, { useEffect, useState } from "react";
import { PageHeader, Modal, Toast, StatusBadge, EmptyState } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { lock } from "@/lib/perm";

import RowActions from "@/components/RowActions";
/**
 * Angajator + Puncte de lucru și sedii (integrat).
 * Un angajator poate avea mai multe puncte de lucru/sedii, iar structurile
 * organizatorice își pot moșteni punctul de lucru de la părinte sau selecta unul propriu.
 */
export default function Employer() {
  const { reloadLicensing, has } = useAuth();
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [licenses, setLicenses] = useState({});
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(false);
  const [locEditing, setLocEditing] = useState(null);
  const [form, setForm] = useState({ name:"",cui:"",address:"",phone:"",email:"",logo_url:"",
    legal_representative_name:"", legal_representative_title:"" });
  const [msg, setMsg] = useState({text:"",type:"info"});
  const [uploading, setUploading] = useState(false);
  // Pe o instalare de client angajatorul vine de la furnizor, cu licența:
  // nu se creează manual, iar denumirea și CUI-ul nu se pot modifica aici.
  const [managedByLicense, setManagedByLicense] = useState(false);
  useEffect(() => {
    api.get("/config/deployment").then(r => setManagedByLicense(!!r.data.is_client_install)).catch(() => {});
  }, []);

  const load = async () => {
    try {
      const [e, l, lic] = await Promise.all([
        api.get("/mdm/employer"),
        api.get("/mdm/locations"),
        api.get("/licenses/active"),
      ]);
      if (e.data) { setEmp(e.data); setForm(e.data); }
      setLocations(l.data || []);
      setLicenses(lic.data || {});
    } catch (err) {
      setMsg({text: err?.response?.data?.detail || "Eroare la încărcare", type: "error"});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name?.trim()) return setMsg({text:"Denumirea este obligatorie",type:"error"});
    if (!form.cui?.trim()) return setMsg({text:"CUI-ul este obligatoriu",type:"error"});
    try { await api.put("/mdm/employer", form); setEditing(false); load(); await reloadLicensing();
      setMsg({text:"Salvat",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e){ setMsg({text: e?.response?.data?.detail || "Eroare", type:"error"}); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/mdm/employer/logo", fd, { headers: {"Content-Type":"multipart/form-data"} });
      setForm({...form, logo_url: r.data.logo_url});
      setMsg({text:"Logo încărcat",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
      load();
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare upload",type:"error"}); }
    finally { setUploading(false); }
  };

  const saveLoc = async () => {
    try {
      if (!locEditing.name?.trim()) { setMsg({text:"Denumire obligatorie",type:"error"}); return; }
      if (locEditing.id) await api.put(`/mdm/locations/${locEditing.id}`, locEditing);
      else await api.post("/mdm/locations", locEditing);
      setLocEditing(null); load();
      setMsg({text:"Punct de lucru salvat",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };
  const toggleLoc = async (loc) => {
    try {
      if (loc.active) { await api.delete(`/mdm/locations/${loc.id}`); }
      else { await api.put(`/mdm/locations/${loc.id}`, {...loc, active: true}); }
      load();
    } catch(e) { setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const logoUrl = (u) => u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

  return (
    <>
      <PageHeader title="Angajator" subtitle="Datele companiei · Puncte de lucru și sedii"
        actions={(emp || !managedByLicense) &&
          <button className="btn btn-primary" onClick={()=>{setForm(emp||form);setEditing(true);}} data-testid="edit-employer" {...lock(has("employer.update"))}>{emp?"Editează":"Creează"}</button>} />
      <Toast msg={msg.text} type={msg.type} />

      <div className="card" style={{padding:26}}>
        {emp ? (
          <div style={{display:"grid",gridTemplateColumns:"180px 1fr",gap:24}}>
            <div>
              {emp.logo_url ? (
                <img src={logoUrl(emp.logo_url)} alt="logo" style={{maxWidth:160,maxHeight:120,objectFit:"contain",border:"1px solid #E2E8F0",borderRadius:6,padding:8}} data-testid="employer-logo-preview" />
              ) : <div style={{width:160,height:120,background:"#F1F5F9",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",color:"#94A3B8",fontSize:12}}>Fără logo</div>}
            </div>
            <div className="grid-2" style={{gap:22}}>
              {[["Denumire","name"],["CUI","cui"],["Adresă","address"],["Telefon","phone"],["Email","email"]].map(([l,k])=>(
                <div key={k}>
                  <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l}</div>
                  <div style={{fontSize:15,fontWeight:500,marginTop:3}}>{emp[k] || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        ) : managedByLicense ? (
          <div style={{color:"#64748B"}} data-testid="employer-pending-activation">
            Angajatorul se configurează automat, cu datele furnizorului, la activarea licenței
            (Parametrii &gt; Licență). Nu trebuie creat manual.
          </div>
        ) : <div style={{color:"#94A3B8"}}>{'Nu există date. Apasă "Creează".'}</div>}
      </div>

      {emp && (
        <div className="card" style={{padding:22, marginTop:16}}>
          <h3 style={{margin:"0 0 4px", fontSize:15, fontWeight:700, color:"var(--navy)"}}>Ordonator de credite / Administrator societate</h3>
          <div style={{fontSize:12, color:"#64748B", marginBottom:14}}>Conducătorul entității juridice — aprobă automat programarea CO în „Semnături Autorizate".</div>
          <div className="grid-2" style={{gap:22}}>
            {[["Nume și prenume","legal_representative_name"],["Funcția","legal_representative_title"]].map(([l,k])=>(
              <div key={k}>
                <div style={{fontSize:11,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l}</div>
                <div style={{fontSize:15,fontWeight:500,marginTop:3}}>{emp[k] || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Puncte de lucru și sedii — integrat */}
      <div className="card" style={{padding:22, marginTop:16}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14}}>
          <div>
            <h3 style={{margin:0, fontSize:15, fontWeight:700, color:"var(--navy)"}}>Puncte de lucru și sedii</h3>
            <div style={{fontSize:12, color:"#64748B", marginTop:2}}>Un angajator poate avea mai multe puncte de lucru sau sedii. Le poți asocia la structurile organizatorice.</div>
          </div>
          <button className="btn btn-primary" onClick={()=>setLocEditing({name:"",address:"",active:true})} data-testid="add-location" {...lock(!!emp && has("locations.create"))}>+ Punct nou</button>
        </div>
        {loading ? null : !emp ? (
          <div style={{padding:12, background:"#FEF2F2", color:"#991B1B", borderRadius:6, fontSize:13}} data-testid="no-employer-banner">
            Salvează mai întâi datele angajatorului (mai sus) înainte de a adăuga puncte de lucru.
          </div>
        ) : locations.length === 0 ? (
          <EmptyState text='Niciun punct de lucru definit. Apasă „+ Punct nou".' />
        ) : (
          <table className="tbl">
            <thead><tr><th>Denumire</th><th>Adresă</th><th>Status</th><th></th></tr></thead>
            <tbody>{locations.map(l => (
              <tr key={l.id} data-testid={`loc-row-${l.name}`}>
                <td style={{fontWeight:600}}>{l.name}</td>
                <td style={{color:"#64748B"}}>{l.address || "—"}</td>
                <td>{l.active ? <span className="badge badge-active">Activ</span> : <span className="badge badge-inactive">Inactiv</span>}</td>
                <td>
                  <RowActions testid={`row-menu-${l.name}`} items={[
                    {label:"Editează", icon:"edit", onClick:()=>setLocEditing({...l}), testid:`edit-loc-${l.name}`, ...lock(has("locations.update"))},
                    {label:l.active ? "Dezactivează" : "Reactivează", icon:l.active ? "close" : "check", danger:l.active, onClick:()=>toggleLoc(l), testid:`toggle-loc-${l.name}`, ...lock(has("locations.deactivate"))},
                  ]} />
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {Object.keys(licenses).length > 0 && (
        <div className="card" style={{padding:22,marginTop:16}}>
          <h3 style={{margin:"0 0 14px",fontSize:15,fontWeight:700,color:"var(--navy)"}}>Licențe active</h3>
          <div style={{display:"grid",gap:10}}>
            {Object.values(licenses).map(l => (
              <div key={l.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"#F5F9FF",borderRadius:6,borderLeft:"3px solid var(--blue-primary)"}} data-testid={`lic-badge-${l.module_key}`}>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,color:"var(--blue-primary)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l.module_label}</div>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--navy)",marginTop:2}}>{l.company_name}</div>
                  <div style={{fontSize:12,color:"#334155",marginTop:2}}>Plan <b>{l.plan_label}</b> · Valabil {l.valid_from} → {l.valid_to} · <b>{l.days_left}</b> zile rămase · {l.current_employments}/{l.max_employments===-1?"∞":l.max_employments} rapoarte de muncă</div>
                </div>
                <StatusBadge status={l.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <Modal title="Angajator" onClose={()=>setEditing(false)} width={640}>
          <div style={{display:"grid",gap:12}}>
            <div>
              <label className="label">Logo (PNG, JPG, JPEG, SVG, WEBP)</label>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                {form.logo_url && <img src={logoUrl(form.logo_url)} alt="logo" style={{maxWidth:80,maxHeight:60,objectFit:"contain",border:"1px solid #E2E8F0",borderRadius:4}} />}
                <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={e=>uploadLogo(e.target.files?.[0])} disabled={uploading} data-testid="emp-logo-file" />
                {uploading && <span style={{fontSize:12,color:"#64748B"}}>Se încarcă...</span>}
              </div>
              <div style={{fontSize:11,color:"#94A3B8",marginTop:4}}>Sau URL extern (opțional):</div>
              <input className="input" placeholder="https://... (opțional)" value={form.logo_url||""} onChange={e=>setForm({...form,logo_url:e.target.value})} data-testid="emp-logo-url" style={{marginTop:4}} />
            </div>
            {[["Denumire","name",true],["CUI","cui",true],["Adresă","address"],["Telefon","phone"],["Email","email"]].map(([l,k,r])=>{
              const locked = managedByLicense && (k === "name" || k === "cui");
              return (
                <div key={k}><label className={"label"+(r?" label-req":"")}>{l}</label>
                  <input className="input" value={form[k]||""} disabled={locked} onChange={e=>setForm({...form,[k]:e.target.value})} data-testid={`emp-${k}`} />
                  {locked && <div style={{fontSize:11,color:"#94A3B8",marginTop:3}}>Stabilit de furnizor, prin licență — nu se poate modifica aici.</div>}
                </div>
              );
            })}
            <div style={{borderTop:"1px solid #E2E8F0",paddingTop:12,marginTop:4}}>
              <div style={{fontSize:12,fontWeight:700,color:"var(--navy)",marginBottom:2}}>Ordonator de credite / Administrator societate</div>
              <div style={{fontSize:11,color:"#64748B"}}>Conducătorul entității juridice — aprobă automat programarea CO.</div>
            </div>
            {[["Nume și prenume","legal_representative_name"],["Funcția","legal_representative_title"]].map(([l,k])=>(
              <div key={k}><label className="label">{l}</label>
                <input className="input" value={form[k]||""} onChange={e=>setForm({...form,[k]:e.target.value})} data-testid={`emp-${k}`} /></div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setEditing(false)}>Anulează</button>
            <button className="btn btn-primary" onClick={save} data-testid="save-employer" {...lock(has("employer.update"))}>Salvează</button>
          </div>
        </Modal>
      )}

      {locEditing && (
        <Modal title={locEditing.id ? "Editează punct de lucru" : "Punct de lucru nou"} onClose={()=>setLocEditing(null)}>
          <div style={{display:"grid",gap:12}}>
            <div><label className="label label-req">Denumire</label>
              <input className="input" value={locEditing.name} onChange={e=>setLocEditing({...locEditing,name:e.target.value})} data-testid="loc-name" /></div>
            <div><label className="label">Adresă</label>
              <input className="input" value={locEditing.address||""} onChange={e=>setLocEditing({...locEditing,address:e.target.value})} data-testid="loc-address" /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setLocEditing(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={saveLoc} data-testid="save-loc" {...lock(has(locEditing.id ? "locations.update" : "locations.create"))}>Salvează</button>
          </div>
        </Modal>
      )}
    </>
  );
}
